/**
 * OptimalWindowCard — live "right now vs best future window" card.
 *
 * Surfaces the GET /api/irrigation/schedules/optimal-window endpoint that
 * scores each candidate hour over the next 18h for irrigation efficiency
 * (lower evap = higher score). Sits between the page header and the
 * schedule builder so the user sees "the engine would NOT fire right now"
 * before they hit Run-now.
 *
 * Two side-by-side tiles:
 *   ┌─ RIGHT NOW (score, quality band, top driver)
 *   └─ BEST FUTURE WINDOW (in N hours, score, quality)
 * + a horizontal mini-strip of the next 6 ranked hours so the user can
 * eyeball when the "all green" zone arrives.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Clock, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { API_URL } from '../config/api';

const QUALITY_STYLE = {
  ideal:    { color: 'text-emerald-500', bg: 'bg-emerald-500/10',  border: 'border-emerald-500/40', label: 'Ideal' },
  ok:       { color: 'text-sky-500',     bg: 'bg-sky-500/10',      border: 'border-sky-500/40',     label: 'OK' },
  marginal: { color: 'text-amber-500',   bg: 'bg-amber-500/10',    border: 'border-amber-500/40',   label: 'Marginal' },
  wasteful: { color: 'text-rose-500',    bg: 'bg-rose-500/10',     border: 'border-rose-500/40',    label: 'Wasteful' },
};

function QualityPill({ quality, score }) {
  const s = QUALITY_STYLE[quality] || QUALITY_STYLE.ok;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${s.color} ${s.bg} border ${s.border} rounded-full px-2 py-0.5`}>
      {quality === 'ideal' ? <CheckCircle2 className="w-3 h-3"/> : quality === 'wasteful' ? <AlertTriangle className="w-3 h-3"/> : null}
      {s.label} · {score ?? '—'}
    </span>
  );
}

function topDriver(window) {
  // Pick the most-impactful negative factor for the explanation line
  const f = window?.factors || {};
  const candidates = [
    { k: 'vpd_kpa',   v: f.vpd_kpa,   tag: 'VPD',   bad: (v) => v >= 1.8 },
    { k: 'solar_wm2', v: f.solar_wm2, tag: 'Solar', bad: (v) => v >= 700 },
    { k: 'wind_mph',  v: f.wind_mph,  tag: 'Wind',  bad: (v) => v >= 15  },
    { k: 'temp_f',    v: f.temp_f,    tag: 'Air',   bad: (v) => v >= 95  },
  ];
  const losers = candidates.filter(c => c.v != null && c.bad(c.v));
  if (losers.length === 0) return null;
  losers.sort((a, b) => b.v - a.v);
  const u = { vpd_kpa: 'kPa', solar_wm2: 'W/m²', wind_mph: 'mph', temp_f: '°F' }[losers[0].k];
  return `${losers[0].tag} ${typeof losers[0].v === 'number' ? losers[0].v.toFixed(losers[0].k === 'vpd_kpa' ? 2 : 0) : losers[0].v} ${u}`;
}

export const OptimalWindowCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API_URL}/api/irrigation/schedules/optimal-window`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <Card className="mb-4" data-testid="optimal-window-card-loading">
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Computing watering windows…
        </CardContent>
      </Card>
    );
  }
  if (err || data?.error) {
    return (
      <Card className="mb-4 border-amber-500/40" data-testid="optimal-window-card-error">
        <CardContent className="py-4 text-sm text-amber-700 dark:text-amber-300">
          <div className="font-semibold">Watering window data unavailable</div>
          <div className="text-xs text-muted-foreground mt-1">
            {data?.reason || err || 'Open-Meteo did not return hourly data. Try again in a few minutes.'}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const cur = data.current || {};
  const best = data.best_future || {};
  const decision = data.decision || {};
  const ranked = data.ranked_next || [];
  // Backend should_postpone() returns `{action: 'postpone' | 'fire_now', reason}`.
  // The first cut of this card read `decision.postpone` (a bool that never
  // existed) so the postpone branch was unreachable. Normalize both shapes.
  const isPostpone = decision.action === 'postpone' || decision.postpone === true;

  const curDriver = topDriver(cur);
  const bestQ = (best.quality || '').toUpperCase();
  const article = /^[AEIOU]/.test(bestQ) ? 'an' : 'a';
  const recommendation =
    isPostpone ? `Postpone ~${best.hours_from_now || 0}h for ${article} ${bestQ} window`
    : cur.quality === 'ideal' ? 'Fire now — conditions are ideal'
    : cur.quality === 'ok' ? 'OK to fire — conditions are acceptable'
    : 'Hold off — better window opening soon';

  return (
    <Card className="mb-4 border-emerald-500/30 bg-emerald-500/[0.02]" data-testid="optimal-window-card">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-baseline justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-semibold">Watering Window · Right Now</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Engine v2</span>
            <Button size="icon" variant="ghost" onClick={refresh} disabled={loading} data-testid="optimal-window-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* RIGHT NOW */}
          <div className={`rounded-lg border p-3 ${QUALITY_STYLE[cur.quality]?.border || 'border-border'} ${QUALITY_STYLE[cur.quality]?.bg || ''}`}
               data-testid="optimal-window-current">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Right Now</div>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold tabular-nums" data-testid="optimal-window-current-score">{cur.score ?? '—'}</div>
              <QualityPill quality={cur.quality} score={cur.score} />
            </div>
            {curDriver && (
              <div className="text-[11px] text-muted-foreground mt-1.5">
                Top driver: <strong>{curDriver}</strong>
              </div>
            )}
          </div>

          {/* BEST FUTURE */}
          <div className={`rounded-lg border p-3 ${QUALITY_STYLE[best.quality]?.border || 'border-border'} ${QUALITY_STYLE[best.quality]?.bg || ''}`}
               data-testid="optimal-window-best">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Best Future Window
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold tabular-nums" data-testid="optimal-window-best-score">
                {best.score != null ? best.score : '—'}
              </div>
              <QualityPill quality={best.quality} score={best.score} />
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
              Opens in <strong>~{best.hours_from_now ?? '—'}h</strong>
            </div>
          </div>
        </div>

        {/* Next-6 strip */}
        {ranked.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50" data-testid="optimal-window-strip">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Next 6 hours · ranked</div>
            <div className="flex gap-1.5 overflow-x-auto">
              {ranked.map((h, i) => {
                const s = QUALITY_STYLE[h.quality] || QUALITY_STYLE.ok;
                return (
                  <div key={i}
                       className={`flex-shrink-0 rounded-md border px-2 py-1 text-center min-w-[58px] ${s.border} ${s.bg}`}
                       data-testid={`optimal-window-hour-${i}`}>
                    <div className="text-[9px] uppercase font-semibold text-muted-foreground">+{h.hours_from_now}h</div>
                    <div className={`text-base font-bold tabular-nums ${s.color}`}>{h.score}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recommendation */}
        <div className="mt-3 pt-3 border-t border-border/50 text-sm" data-testid="optimal-window-recommendation">
          <span className="text-muted-foreground">Recommendation: </span>
          <strong className={
            cur.quality === 'ideal' ? 'text-emerald-600 dark:text-emerald-400'
            : isPostpone ? 'text-amber-600 dark:text-amber-400'
            : 'text-foreground'
          }>{recommendation}</strong>
        </div>
      </CardContent>
    </Card>
  );
};
